import Modal from "bootstrap/js/dist/modal"
import { showToast } from "./shared"

export function initStoreUi() {
  initWishlistButtons()
  initProductGalleries()
  initProductRails()
  initReviewRatings()
  initReviewBodies()
  initReviewCounters()
  initReviewCouponModal()
  initProductPurchaseForms()
  initCheckoutAccountForms()
  initInquiryForms()
  initAccountOrderMore()
  initAffiliateDashboard()
}

function initWishlistButtons() {
  document.querySelectorAll("[data-wishlist-button]").forEach((button) => {
    const form = button.closest("form")
    if (!form || form.dataset.wishlistBound === "true") return

    form.dataset.wishlistBound = "true"
    form.addEventListener("submit", async (event) => {
      event.preventDefault()
      if (button.disabled) return

      button.disabled = true
      try {
        const response = await fetch(form.action, {
          method: form.method.toUpperCase(),
          headers: {
            "Accept": "application/json",
            "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
          },
          body: new FormData(form)
        })

        if (!response.ok) throw new Error("wishlist request failed")

        const data = await response.json()
        syncWishlistButtons(button.dataset.productId, data.wishlisted)
        document.querySelectorAll("[data-wishlist-count]").forEach((count) => {
          count.textContent = data.count
        })
        showToast(data.message, "notice")
      } catch (_error) {
        showToast("お気に入りを更新できませんでした。", "alert")
      } finally {
        button.disabled = false
      }
    })
  })
}

function syncWishlistButtons(productId, wishlisted) {
  document.querySelectorAll(`[data-wishlist-button][data-product-id="${productId}"]`).forEach((button) => {
    button.classList.toggle("active", wishlisted)
    button.textContent = wishlisted ? button.dataset.activeLabel : button.dataset.inactiveLabel
    const title = wishlisted ? button.dataset.activeTitle : button.dataset.inactiveTitle
    button.title = title
    button.setAttribute("aria-label", title)
  })
}

function initProductGalleries() {
  document.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
    const images = [...gallery.querySelectorAll("[data-gallery-image]")]
    const thumbs = [...gallery.querySelectorAll("[data-gallery-thumb]")]
    if (images.length < 2) return

    let currentIndex = 0
    const current = gallery.querySelector("[data-gallery-current]")
    const show = (index) => {
      currentIndex = (index + images.length) % images.length
      images.forEach((image, imageIndex) => image.classList.toggle("active", imageIndex === currentIndex))
      thumbs.forEach((thumb, thumbIndex) => {
        const active = thumbIndex === currentIndex
        thumb.classList.toggle("active", active)
        if (active) {
          thumb.setAttribute("aria-current", "true")
        } else {
          thumb.removeAttribute("aria-current")
        }
      })
      if (current) current.textContent = currentIndex + 1
    }

    gallery.querySelector("[data-gallery-prev]")?.addEventListener("click", () => show(currentIndex - 1))
    gallery.querySelector("[data-gallery-next]")?.addEventListener("click", () => show(currentIndex + 1))
    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", () => show(Number(thumb.dataset.galleryThumb)))
    })
  })
}

function initProductRails() {
  document.querySelectorAll("[data-product-rail]").forEach((rail) => {
    const track = rail.querySelector("[data-rail-track]")
    if (!track) return

    const step = Number(rail.dataset.railStep || 4)
    const scrollGroup = (direction) => {
      const item = track.querySelector(".rail-item")
      const gap = parseFloat(window.getComputedStyle(track).columnGap) || 0
      const distance = item ? (item.getBoundingClientRect().width + gap) * step : track.clientWidth
      track.scrollBy({ left: direction * distance, behavior: "smooth" })
    }

    rail.querySelector("[data-rail-prev]")?.addEventListener("click", () => {
      scrollGroup(-1)
    })
    rail.querySelector("[data-rail-next]")?.addEventListener("click", () => {
      scrollGroup(1)
    })
    if (rail.dataset.railAuto === "true" && track.scrollWidth > track.clientWidth) {
      window.setInterval(() => {
        const nearEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4
        if (nearEnd) {
          track.scrollTo({ left: 0, behavior: "smooth" })
        } else {
          scrollGroup(1)
        }
      }, 5200)
    }
  })
}

function initReviewRatings() {
  document.querySelectorAll("[data-review-rating]").forEach((picker) => {
    const input = picker.querySelector("[data-review-rating-input]")
    const stars = [...picker.querySelectorAll("[data-rating-star]")]
    if (!input || stars.length === 0) return

    const sync = (value) => {
      input.value = value
      stars.forEach((star) => {
        star.classList.toggle("active", Number(star.dataset.ratingStar) <= value)
      })
    }

    stars.forEach((star) => {
      star.addEventListener("click", () => sync(Number(star.dataset.ratingStar)))
    })
    sync(Number(input.value) || 5)
  })
}

function initReviewBodies() {
  document.querySelectorAll("[data-review-body]").forEach((body) => {
    const toggle = body.querySelector("[data-review-toggle]")
    const shortBody = body.querySelector("[data-review-short]")
    const fullBody = body.querySelector("[data-review-full]")
    if (!toggle || !shortBody || !fullBody) return

    toggle.addEventListener("click", () => {
      const opening = fullBody.hidden
      fullBody.hidden = !opening
      shortBody.hidden = opening
      toggle.textContent = opening ? toggle.dataset.closeLabel : toggle.dataset.openLabel
    })
  })
}

function initReviewCounters() {
  document.querySelectorAll("[data-review-text]").forEach((textarea) => {
    const counter = textarea.closest(".review-text-wrap")?.querySelector("[data-review-count]")
    if (!counter) return

    const max = Number(textarea.getAttribute("maxlength")) || 0
    const sync = () => {
      counter.textContent = `${textarea.value.length}/${max}`
    }

    textarea.addEventListener("input", sync)
    sync()
  })
}

function initReviewCouponModal() {
  const dialog = document.querySelector("[data-review-coupon-modal]")
  if (!dialog || dialog.dataset.reviewCouponBound === "true") return
  dialog.dataset.reviewCouponBound = "true"

  const copyButton = dialog.querySelector("[data-review-coupon-copy]")
  const code = dialog.querySelector("[data-review-coupon-code]")?.textContent?.trim()
  if (copyButton && code) {
    copyButton.addEventListener("click", async () => {
      try {
        await navigator.clipboard.writeText(code)
        showToast("クーポンコードをコピーしました")
      } catch {
        showToast("コピーできませんでした。コードを直接ご利用ください")
      }
    })
  }

  const modal = Modal.getOrCreateInstance(dialog)
  document.addEventListener("turbo:before-cache", () => modal.hide(), { once: true })
  modal.show()
}

function initProductPurchaseForms() {
  document.querySelectorAll("[data-product-purchase]").forEach((form) => {
    if (form.dataset.productPurchaseBound === "true") return

    form.dataset.productPurchaseBound = "true"
    const variants = JSON.parse(form.dataset.variants || "[]")
    const quantitySelect = form.querySelector("[data-quantity-select]")
    const speedSelect = form.querySelector("[data-speed-select]")
    const variantId = form.querySelector("[data-variant-id]")
    const prices = form.querySelectorAll("[data-variant-price]")
    const targetIdentifier = form.querySelector("[data-target-identifier]")

    const syncVariant = () => {
      const variant = variants.find((item) => {
        return String(item.quantity) === quantitySelect?.value && (!speedSelect || item.speed === speedSelect.value)
      }) || variants.find((item) => {
        return String(item.quantity) === quantitySelect?.value
      }) || variants[0]

      if (!variant) return
      variantId.value = variant.id
      prices.forEach((price) => { price.textContent = variant.price })
    }

    quantitySelect?.addEventListener("change", syncVariant)
    speedSelect?.addEventListener("change", syncVariant)
    syncVariant()
    form.querySelectorAll("[data-agreement-check]").forEach((checkbox) => {
      checkbox.addEventListener("change", () => {
        checkbox.closest("[data-notice-item]")?.classList.toggle("invalid", !checkbox.checked)
      })
    })
    targetIdentifier?.addEventListener("input", () => targetIdentifier.classList.remove("invalid"))
    form.addEventListener("submit", (event) => {
      const invalidAgreements = [...form.querySelectorAll("[data-agreement-check]")].filter((checkbox) => !checkbox.checked)
      const missingTarget = targetIdentifier && targetIdentifier.value.trim() === ""
      const targetLabel = form.dataset.targetLabel || "URLまたはID"

      form.querySelectorAll("[data-notice-item].invalid").forEach((item) => item.classList.remove("invalid"))
      targetIdentifier?.classList.remove("invalid")

      if (missingTarget || invalidAgreements.length > 0) {
        event.preventDefault()

        invalidAgreements.forEach((checkbox) => {
          checkbox.closest("[data-notice-item]")?.classList.add("invalid")
        })
        if (missingTarget) targetIdentifier.classList.add("invalid")

        const firstInvalid = missingTarget ? targetIdentifier : invalidAgreements[0]?.closest("[data-notice-item]")
        firstInvalid?.scrollIntoView({ behavior: "smooth", block: "center" })
        showToast(missingTarget ? `${targetLabel}を入力してください。` : "確認項目に同意してください。", "alert")
      }
    })
    syncVariant()
  })
}

function initCheckoutAccountForms() {
  document.querySelectorAll("[data-checkout-account]").forEach((panel) => {
    if (panel.dataset.checkoutAccountBound === "true") return

    panel.dataset.checkoutAccountBound = "true"
    const toggle = panel.querySelector("[data-checkout-account-toggle]")
    const fields = panel.querySelector("[data-checkout-account-fields]")
    const requiredInputs = [...panel.querySelectorAll("[data-checkout-account-required]")]

    if (!toggle || !fields) return

    const sync = () => {
      const enabled = toggle.checked
      fields.hidden = !enabled
      requiredInputs.forEach((input) => {
        input.required = enabled
        if (!enabled) input.value = ""
      })
    }

    toggle.addEventListener("change", sync)
    sync()
  })
}

function initInquiryForms() {
  document.querySelectorAll("[data-inquiry-form]").forEach((form) => {
    if (form.dataset.inquiryBound === "true") return

    form.dataset.inquiryBound = "true"
    const categorySelect = form.querySelector("[data-inquiry-category-select]")
    const radios = [...form.querySelectorAll("[data-inquiry-category-radio]")]
    const attachmentWrap = form.querySelector("[data-inquiry-attachment]")
    const attachmentInput = attachmentWrap?.querySelector("input[type='file']")
    const attachmentLabel = form.querySelector("[data-inquiry-attachment-label]")
    const attachmentHelp = form.querySelector("[data-inquiry-attachment-help]")
    const attachmentName = form.querySelector("[data-file-name]")
    const reductionWrap = form.querySelector("[data-inquiry-reduction]")
    const reductionInput = reductionWrap?.querySelector("input")
    const orderNumberWrap = form.querySelector("[data-inquiry-order-field]")
    const orderNumberInput = form.querySelector("[data-inquiry-order-number]")
    const orderRequiredMark = form.querySelector("[data-inquiry-order-required]")
    const orderUnknownInput = form.querySelector("[data-inquiry-order-unknown]")
    const selectedOrdersWrap = form.querySelector("[data-inquiry-selected-orders]")
    const selectedOrdersEmpty = form.querySelector("[data-inquiry-selected-empty]")
    const selectedOrderInputs = form.querySelector("[data-inquiry-selected-order-inputs]")
    const orderOptions = [...document.querySelectorAll("[data-inquiry-order-option]")]
    const orderSearch = document.querySelector("[data-inquiry-order-search]")
    const help = form.querySelector("[data-inquiry-kind-help]")
    const selectedOrderIds = new Set([...form.querySelectorAll("[data-inquiry-selected-order-input]")].map((input) => input.value))

    const selectedCategory = () => categorySelect?.selectedOptions?.[0] || radios.find((radio) => radio.checked)
    const hasOrderReference = () => {
      return orderUnknownInput?.checked || orderNumberInput?.value.trim() !== "" || selectedOrderIds.size > 0
    }

    const syncSelectedOrders = () => {
      if (!selectedOrdersWrap || !selectedOrderInputs) return

      const selectedOptions = orderOptions.filter((option) => selectedOrderIds.has(option.dataset.orderId))
      selectedOrdersWrap.querySelectorAll("[data-inquiry-selected-chip]").forEach((chip) => chip.remove())
      selectedOrderInputs.innerHTML = ""
      if (selectedOrdersEmpty) selectedOrdersEmpty.hidden = selectedOptions.length > 0

      selectedOptions.forEach((option) => {
        const input = document.createElement("input")
        input.type = "hidden"
        input.name = "order_ids[]"
        input.value = option.dataset.orderId
        input.dataset.inquirySelectedOrderInput = "true"
        selectedOrderInputs.appendChild(input)

        const chip = document.createElement("button")
        chip.type = "button"
        chip.className = "inquiry-selected-order-chip"
        chip.dataset.inquirySelectedChip = "true"
        chip.dataset.orderId = option.dataset.orderId
        chip.innerHTML = `<span>${option.dataset.orderLabel}</span><i class="bi bi-x" aria-hidden="true"></i>`
        selectedOrdersWrap.appendChild(chip)
      })

      orderOptions.forEach((option) => {
        const selected = selectedOrderIds.has(option.dataset.orderId)
        option.classList.toggle("selected", selected)
        option.setAttribute("aria-pressed", selected ? "true" : "false")
      })
    }

    const sync = () => {
      const selected = selectedCategory()
      if (!selected) return

      const usesAttachment = selected.dataset.attachmentEnabled === "true"
      const usesReduction = selected.dataset.reductionCountEnabled === "true"
      const requiresOrderNumber = selected.dataset.orderNumberRequired === "true"
      const usesHelp = selected.dataset.helpEnabled === "true" && selected.dataset.helpText.trim() !== ""
      const attachmentHelpText = selected.dataset.attachmentHelpText.trim()

      if (attachmentWrap) attachmentWrap.hidden = !usesAttachment
      if (attachmentInput) attachmentInput.required = usesAttachment
      if (attachmentLabel) attachmentLabel.textContent = selected.dataset.attachmentLabel || "添付ファイル"
      if (attachmentHelp) {
        attachmentHelp.hidden = !usesAttachment || attachmentHelpText === ""
        attachmentHelp.textContent = attachmentHelpText
      }

      if (reductionWrap) reductionWrap.hidden = !usesReduction
      if (reductionInput) reductionInput.required = usesReduction
      if (orderNumberWrap) orderNumberWrap.hidden = !requiresOrderNumber
      if (orderNumberInput) {
        orderNumberInput.required = false
        if (!requiresOrderNumber) {
          orderNumberInput.value = ""
          orderNumberInput.classList.remove("invalid")
        }
      }
      if (orderUnknownInput) {
        if (!requiresOrderNumber) orderUnknownInput.checked = false
      }
      if (!requiresOrderNumber) {
        selectedOrderIds.clear()
        syncSelectedOrders()
      }
      if (orderRequiredMark) orderRequiredMark.hidden = !requiresOrderNumber

      if (help) {
        help.hidden = !usesHelp
        help.textContent = selected.dataset.helpText
      }
    }

    categorySelect?.addEventListener("change", sync)
    radios.forEach((radio) => radio.addEventListener("change", sync))
    attachmentInput?.addEventListener("change", () => {
      if (!attachmentName) return
      attachmentName.textContent = attachmentInput.files?.[0]?.name || "画像またはPDFをアップロードできます"
    })
    orderOptions.forEach((option) => {
      option.addEventListener("click", () => {
        const orderId = option.dataset.orderId
        if (!orderId) return

        if (selectedOrderIds.has(orderId)) {
          selectedOrderIds.delete(orderId)
        } else {
          selectedOrderIds.add(orderId)
        }
        syncSelectedOrders()
      })
    })
    selectedOrdersWrap?.addEventListener("click", (event) => {
      const chip = event.target.closest("[data-inquiry-selected-chip]")
      if (!chip) return

      selectedOrderIds.delete(chip.dataset.orderId)
      syncSelectedOrders()
    })
    orderSearch?.addEventListener("input", () => {
      const query = orderSearch.value.trim().toLowerCase()
      orderOptions.forEach((option) => {
        option.hidden = query !== "" && !option.dataset.orderSearch.includes(query)
      })
    })
    form.addEventListener("submit", (event) => {
      const selected = selectedCategory()
      const fields = [
        form.querySelector("[name='email']"),
        form.querySelector("[name='message']")
      ]

      if (selected?.dataset.attachmentEnabled === "true") fields.push(attachmentInput)
      if (selected?.dataset.reductionCountEnabled === "true") fields.push(reductionInput)

      const missingFields = fields.filter((field) => field && field.value.trim() === "")
      const missingOrderReference = selected?.dataset.orderNumberRequired === "true" && !hasOrderReference()
      form.querySelectorAll(".invalid").forEach((field) => field.classList.remove("invalid"))

      if (missingOrderReference) orderNumberWrap?.classList.add("invalid")

      if (!selected || missingFields.length > 0 || missingOrderReference) {
        event.preventDefault()
        missingFields.forEach((field) => field.classList.add("invalid"))
        const firstInvalid = missingOrderReference ? orderNumberWrap : (missingFields[0] || form.querySelector(".inquiry-kind"))
        firstInvalid?.scrollIntoView({ behavior: "smooth", block: "center" })
        showToast("未入力の必須項目があります。", "alert")
      }
    })
    syncSelectedOrders()
    sync()
  })
}

function initAffiliateDashboard() {
  document.querySelectorAll("[data-affiliate-tabs]").forEach((tabs) => {
    if (tabs.dataset.tabsBound === "true") return

    tabs.dataset.tabsBound = "true"
    const card = tabs.closest(".account-card")
    const seeAll = card?.querySelector("[data-affiliate-see-all]")
    const buttons = [...tabs.querySelectorAll("[data-tab]")]
    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        buttons.forEach((other) => other.classList.toggle("active", other === button))
        card?.querySelectorAll("[data-tab-panel]").forEach((panel) => {
          panel.hidden = panel.dataset.tabPanel !== button.dataset.tab
        })
        if (seeAll) {
          seeAll.hidden = button.dataset.tab === "chart"
          if (button.dataset.tab === "rewards") seeAll.href = seeAll.dataset.rewardsHref
          if (button.dataset.tab === "clicks") seeAll.href = seeAll.dataset.clicksHref
        }
      })
    })
  })

  document.querySelectorAll("[data-copy-target]").forEach((button) => {
    if (button.dataset.copyBound === "true") return

    button.dataset.copyBound = "true"
    const icon = button.querySelector("[data-copy-icon]")
    const label = button.querySelector("[data-copy-label]")
    const originalLabel = label?.innerHTML
    let restoreTimer

    button.addEventListener("click", async () => {
      const source = document.getElementById(button.dataset.copyTarget)
      if (!source) return

      try {
        await navigator.clipboard.writeText(source.textContent.trim())
      } catch (_error) {
        showToast("リンクをコピーできませんでした。", "alert")
        return
      }

      icon?.classList.remove("bi-clipboard")
      icon?.classList.add("bi-check-lg")
      if (label) label.textContent = "コピーしました"
      window.clearTimeout(restoreTimer)
      restoreTimer = window.setTimeout(() => {
        icon?.classList.add("bi-clipboard")
        icon?.classList.remove("bi-check-lg")
        if (label) label.innerHTML = originalLabel
      }, 1600)
    })
  })
}

function initAccountOrderMore() {
  const list = document.querySelector("[data-account-order-list]")
  const button = document.querySelector("[data-account-order-more]")
  if (!list || !button || list.dataset.moreBound === "true") return

  list.dataset.moreBound = "true"
  const step = Number(list.dataset.visibleCount || 5)
  const items = [...list.querySelectorAll("[data-account-order-item]")]
  let visibleCount = items.filter((item) => !item.hidden).length || step

  const sync = () => {
    items.forEach((item, index) => {
      item.hidden = index >= visibleCount
    })
    button.hidden = visibleCount >= items.length
  }

  button.addEventListener("click", () => {
    visibleCount += step
    sync()
  })

  sync()
}
